import { createContext, useContext, FC, ReactNode, useEffect, useRef, useCallback } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';

import { useLocation } from 'react-router';

interface FiltersContextProps {
  filters: Filters;
  updateFilter: (
    key: keyof Filters,
    value: string | boolean | PetType | PetStatus | PetAges | PetGender,
  ) => void;
  updateFilters: (newFilters: Filters) => void;
  resetFilters: () => void;
}

const FiltersContext = createContext<FiltersContextProps | undefined>(undefined);

const FILTERS_KEY = 'rescatapp_filters';

export const FiltersProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const {
    storedValue: filters,
    setValue: setFilters,
    removeValue: clearFilters,
  } = useLocalStorage<Filters>(FILTERS_KEY, {});

  const location = useLocation();
  const lastStatus = useRef<string | null>(null);

  const updateFilter = (
    key: keyof Filters,
    value: string | boolean | PetType | PetStatus | PetAges | PetGender,
  ) => {
    setFilters((prevFilters) => {
      if (key === 'city' && value === '') return prevFilters;
      return {
        ...prevFilters,
        [key]: value,
      };
    });
  };

  const updateFilters = (newFilters: Filters) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  const resetFilters = useCallback(() => {
    clearFilters();
  }, [clearFilters]);

  useEffect(() => {
    const path = location.pathname;

    const currentStatus = path.includes('/lost')
      ? 'lost'
      : path.includes('/found')
        ? 'found'
        : null;

    if (currentStatus && lastStatus.current && currentStatus !== lastStatus.current) {
      resetFilters();
    }

    if (currentStatus) {
      lastStatus.current = currentStatus;
    }
  }, [location.pathname, resetFilters]);
  return (
    <FiltersContext.Provider value={{ filters, updateFilter, updateFilters, resetFilters }}>
      {children}
    </FiltersContext.Provider>
  );
};

export const useFilters = () => {
  const context = useContext(FiltersContext);
  if (!context) {
    throw new Error('useFilters must be used within a FiltersProvider');
  }
  return context;
};
